import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";

/**
 * Rehearsal Room · Room 04 — GESTURE
 * ===================================
 * 4 short rounds. Each round shows one phrase and 4 gesture figures.
 * The user picks the gesture that best supports the phrase.
 *
 * Presenter enters walking from the LEFT (continuation of Room 03 exit),
 * shrinks during the exercise, then enlarges for the debrief + reflection.
 */

const G = (id, best) => ({ id, src: `/audit/gestures/${id.replace(/_/g, "-")}.webp`, best });
const GESTURES = {
  count:                G("count"),
  frame:                G("frame"),
  open_palms:           G("open_palms"),
  neutral:              G("neutral"),
  expand_outward:       G("expand_outward"),
  move_upward:          G("move_upward"),
  show_level:           G("show_level"),
  contrast_left_right:  G("contrast_left_right"),
  divide_space:         G("divide_space"),
  open_invitation:      G("open_invitation"),
};

const ROUNDS = [
  {
    phraseId: "three_priorities",
    phrase: "There are three priorities we need to focus on.",
    best: "count",
    options: ["count", "open_palms", "neutral", "frame"],
  },
  {
    phraseId: "move_stronger_position",
    phrase: "We need to move from the current situation to a stronger position.",
    best: "move_upward",
    options: ["show_level", "move_upward", "expand_outward", "neutral"],
  },
  {
    phraseId: "two_paths",
    phrase: "These are the two paths in front of us.",
    best: "contrast_left_right",
    options: ["divide_space", "contrast_left_right", "frame", "open_palms"],
  },
  {
    phraseId: "bring_you_in",
    phrase: "I\u2019d like to bring you into this conversation.",
    best: "open_invitation",
    options: ["open_invitation", "expand_outward", "neutral", "count"],
  },
];

const INTRO_STEPS = [
  "Now let\u2019s look at gesture.",
  "A gesture becomes useful when it supports the meaning.",
  "You\u2019ll see a short phrase.",
  "Choose the gesture that best helps that phrase land.",
];

// Bubbles shown AFTER all rounds are done, BEFORE the reflection cards.
const REFLECTION_INTRO_STEPS = [
  "Good.",
  "Now think about how you made those choices.",
  "What guided you most when choosing a gesture?",
];

// The reflection cards themselves — only the labels are shown to the user.
const REFLECTION_OPTIONS = [
  { id: "words",    label: "The words themselves" },
  { id: "meaning",  label: "The meaning I wanted to make clearer" },
  { id: "audience", label: "How I imagined the audience receiving the message" },
  { id: "natural",  label: "What felt most natural to me" },
];

// Bubbles shown AFTER the user selects a reflection card.
const REFLECTION_OUTRO_STEPS = [
  "Good. Keep that in mind.",
  "The gesture we choose often reveals what we\u2019re paying attention to.",
  "Now let\u2019s see what happens when the whole body enters the space.",
  "Come with me.",
];

const DEBRIEF = "Good. Gestures work best when they carry meaning, not when they decorate the words.";

const PRESENTER_MASK =
  "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)";
const EASE = [0.16, 1, 0.3, 1];
const MODE_TRANSITION = { duration: 0.9, ease: EASE };

export function RehearsalRoomGesture() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
    next,
  } = useAudit();

  // Walk-in: from -30% right → normal position, mirrors Room 03 exit.
  const [walkedIn, setWalkedIn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setWalkedIn(true), 900);
    return () => clearTimeout(t);
  }, []);

  const introStep         = answers.gesture?.introStep         ?? 0;
  const roundIndex        = answers.gesture?.roundIndex        ?? 0;
  const debriefSeen       = answers.gesture?.debriefSeen       ?? false;
  const reflectIntroStep  = answers.gesture?.reflectIntroStep  ?? 0;
  const reflectionPick    = answers.gesture?.reflection?.selected ?? null;
  const reflectOutroStep  = answers.gesture?.reflectOutroStep  ?? 0;
  const picks             = answers.gesture?.picks             ?? [];
  const introDone         = introStep >= INTRO_STEPS.length;
  const allRoundsDone     = picks.length >= ROUNDS.length;
  const reflectIntroDone  = reflectIntroStep >= REFLECTION_INTRO_STEPS.length;
  const reflectOutroDone  = reflectOutroStep >= REFLECTION_OUTRO_STEPS.length;

  // Phase machine
  //   rounds  → debrief bubble  → reflection-intro bubbles →
  //   reflection cards          → reflection-outro bubbles → next room
  const inDebrief       = allRoundsDone && !debriefSeen;
  const inReflectIntro  = allRoundsDone && debriefSeen && !reflectIntroDone;
  const inReflectPick   = allRoundsDone && reflectIntroDone && !reflectionPick;
  const inReflectOutro  = allRoundsDone && reflectionPick && !reflectOutroDone;
  const isLeaving       = allRoundsDone && reflectionPick && reflectOutroDone;
  const modeCoaching    = !introDone || inDebrief || inReflectIntro || inReflectOutro || isLeaving;

  // Bubble + Continue gate
  useEffect(() => {
    if (!walkedIn)         { setOverrideMessage(""); setCanProceed(false); return; }
    if (!introDone)        { setOverrideMessage(INTRO_STEPS[introStep]); setCanProceed(false); return; }
    if (!allRoundsDone)    { setOverrideMessage(""); setCanProceed(false); return; }
    if (inDebrief)         { setOverrideMessage(DEBRIEF); setCanProceed(false); return; }
    if (inReflectIntro)    { setOverrideMessage(REFLECTION_INTRO_STEPS[reflectIntroStep]); setCanProceed(false); return; }
    if (inReflectPick)     { setOverrideMessage(""); setCanProceed(false); return; }
    if (inReflectOutro)    { setOverrideMessage(REFLECTION_OUTRO_STEPS[reflectOutroStep]); setCanProceed(false); return; }
    // reflection complete → allow Continue
    setOverrideMessage(REFLECTION_OUTRO_STEPS[REFLECTION_OUTRO_STEPS.length - 1]);
    setCanProceed(true);
  }, [walkedIn, introDone, introStep, allRoundsDone, inDebrief, inReflectIntro, reflectIntroStep, inReflectPick, inReflectOutro, reflectOutroStep, setCanProceed, setOverrideMessage]);

  // Continue interceptor — walking exit before actual next()
  useEffect(() => {
    setBeforeNext(() => {
      if (!isLeaving) return false;
      setTimeout(() => { setBeforeNext(null); next(); }, 850);
      return false;
    });
    return () => setBeforeNext(null);
  }, [isLeaving, setBeforeNext, next]);

  // Persist tags
  useEffect(() => {
    setTagsForRoom("gest", picks.map((p) => `gest:${p.selected}`));
  }, [picks.length, setTagsForRoom, picks]);

  const advance = () => {
    if (!introDone)      return setRoomAnswer("gesture", { introStep: introStep + 1 });
    if (inDebrief)       return setRoomAnswer("gesture", { debriefSeen: true });
    if (inReflectIntro)  return setRoomAnswer("gesture", { reflectIntroStep: reflectIntroStep + 1 });
    if (inReflectOutro)  return setRoomAnswer("gesture", { reflectOutroStep: reflectOutroStep + 1 });
  };

  const pick = (gestureId) => {
    if (!introDone || allRoundsDone) return;
    tapFeedback();
    const cur = ROUNDS[roundIndex];
    const nextPicks = [...picks, { round: roundIndex + 1, phraseId: cur.phraseId, selected: gestureId }];
    setRoomAnswer("gesture", { picks: nextPicks, roundIndex: roundIndex + 1 });
  };

  const pickReflection = (optionId) => {
    if (!inReflectPick) return;
    const tag = `gesture_reflection:${optionId}`;
    setRoomAnswer("gesture", {
      reflection: { selected: optionId, tag },
      reflectOutroStep: 0,
    });
    // Store in the SHARED global reflections collection for downstream use.
    setReflection("gesture", {
      question: "What guided you most when choosing a gesture?",
      selected: optionId,
      tag,
    });
    // Add the granular reflection tag to the tag stream.
    setTagsForRoom("gest_reflection", [tag]);
  };

  const interactive = walkedIn && introDone && !allRoundsDone;
  const inCatcher   = walkedIn && (!introDone || inDebrief || inReflectIntro || inReflectOutro);

  // Presenter
  const presenter = selectedPresenter;
  const pose = !walkedIn || isLeaving ? "walking"
    : modeCoaching ? "presentingAlt" : "listening";
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";
  // Flip horizontally when exiting (walking-off) and for the `listening`
  // pose so the presenter faces the interaction area instead of away.
  const flip = isLeaving || pose === "listening";

  const currentRound = ROUNDS[roundIndex];
  const catcherStep = !introDone ? introStep + 1
    : inDebrief ? 1
    : inReflectIntro ? reflectIntroStep + 1
    : reflectOutroStep + 1;
  const catcherTotal = !introDone ? INTRO_STEPS.length
    : inDebrief ? 1
    : inReflectIntro ? REFLECTION_INTRO_STEPS.length
    : REFLECTION_OUTRO_STEPS.length;

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="rehearsal-room-gesture" style={{ background: "#141310" }}>
      <div aria-hidden="true" className="absolute inset-0"
        style={{ backgroundImage: "url(/audit/bg_gesture.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* Subtle scrim */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{ background: "rgba(10,8,5,0.24)", zIndex: 5 }} />

      {/* Presenter */}
      {presenter && (
        <motion.div
          className="absolute overflow-hidden"
          animate={{
            left: !walkedIn ? "-25%" : (isLeaving ? "-30%" : (modeCoaching ? "-4%" : "-6%")),
            bottom: modeCoaching ? "-5.65%" : "-5%",
            height: modeCoaching ? "91.2%" : "80%",
            opacity: isLeaving ? 0 : 1,
          }}
          initial={false}
          transition={isLeaving ? { duration: 0.85, ease: [0.4, 0, 0.2, 1] } : MODE_TRANSITION}
          style={{ zIndex: 20 }}
          data-testid="gesture-presenter"
        >
          <PresenterFloorShadow />
          <AnimatePresence initial={false} mode="popLayout">
            <motion.img
              key={pose}
              src={presenterSrc}
              alt={presenter.name}
              draggable={false}
              initial={{ opacity: 0, y: 4, scaleX: flip ? -1 : 1 }}
              animate={{ opacity: 1, y: 0, scaleX: flip ? -1 : 1 }}
              exit={{ opacity: 0, y: -4, scaleX: flip ? -1 : 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="h-full w-auto select-none block"
              style={{ mixBlendMode: "multiply", filter: "brightness(0.95) sepia(0.1)", WebkitMaskImage: PRESENTER_MASK, maskImage: PRESENTER_MASK }}
              data-testid={`gesture-presenter-img-${pose}`}
            />
          </AnimatePresence>
        </motion.div>
      )}

      {/* Speech bubble — only during coaching modes */}
      <AnimatePresence initial={false}>
        {walkedIn && modeCoaching && !isLeaving && overrideMessage ? (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={{ top: 60, left: 340, zIndex: 100, maxWidth: 400 }}
            data-testid="gesture-bubble"
          >
            <div className="relative bg-white text-[#1B1B18] px-7 py-5 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 30 }}>
              <p className="font-serif text-xl leading-snug">{overrideMessage}</p>
              <span aria-hidden="true" className="absolute"
                style={{ left: 40, bottom: -16, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Corner mark */}
      <div className="absolute right-6 top-5 flex flex-col items-end pointer-events-none" style={{ zIndex: 50 }}>
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          the rehearsal room
        </p>
      </div>

      {/* Phrase + gesture options — visible while user is picking */}
      <AnimatePresence>
        {interactive && currentRound && (
          <motion.div
            key={`round-${roundIndex}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="absolute"
            style={{ top: "20%", left: "28%", right: "6%", zIndex: 30 }}
            data-testid={`gesture-round-${roundIndex + 1}`}
          >
            {/* Phrase box */}
            <div className="px-8 py-5 md:px-10 md:py-6 shadow-[0_14px_36px_rgba(0,0,0,0.25)]"
              style={{ background: "rgba(242, 235, 218, 0.9)", border: "1px solid rgba(90,60,25,0.14)" }}>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-ink text-center">
                {currentRound.phrase}
              </p>
            </div>

            {/* Round progress dots */}
            <div className="mt-4 flex items-center justify-center gap-2" data-testid="gesture-progress">
              {ROUNDS.map((_, i) => (
                <span key={i} className="block rounded-full"
                  style={{ width: 8, height: 8,
                    background: i < roundIndex ? "#90533b" : i === roundIndex ? "#b9832c" : "rgba(255,255,255,0.35)" }} />
              ))}
            </div>

            {/* Gesture options */}
            <div className="mt-6 grid grid-cols-4 gap-4">
              {currentRound.options.map((gid) => (
                <button
                  key={gid}
                  type="button"
                  onClick={() => pick(gid)}
                  data-testid={`gesture-option-${gid}`}
                  className="group relative bg-white/40 hover:bg-white/70 border border-ink/10 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.3)] p-2"
                  style={{ backdropFilter: "blur(2px)" }}
                >
                  <img src={GESTURES[gid].src} alt="" draggable={false} loading="lazy" decoding="async"
                    className="w-full h-[170px] object-contain select-none block" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reflection cards — shown after the reflection-intro bubbles,
          before the reflection-outro bubbles. */}
      <AnimatePresence>
        {walkedIn && inReflectPick && (
          <motion.div
            key="reflection-cards"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="absolute grid grid-cols-2 gap-4"
            style={{ top: "26%", left: "28%", right: "6%", zIndex: 40 }}
            data-testid="gesture-reflection-cards"
          >
            {REFLECTION_OPTIONS.map((opt) => {
              const selected = reflectionPick === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => pickReflection(opt.id)}
                  data-testid={`gesture-reflection-${opt.id}`}
                  className="text-left px-6 py-5 transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: selected
                      ? "linear-gradient(180deg, #E9C88A 0%, #B9832C 100%)"
                      : "rgba(242, 235, 218, 0.88)",
                    border: selected
                      ? "2px solid #90533b"
                      : "1px solid rgba(90,60,25,0.16)",
                    boxShadow: selected
                      ? "0 14px 40px rgba(144,83,59,0.35)"
                      : "0 6px 18px rgba(0,0,0,0.16)",
                    color: selected ? "#1B1B18" : "#1B1B18",
                  }}
                >
                  <p className="font-serif text-xl md:text-2xl leading-snug">{opt.label}</p>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap catcher for intro + debrief/reflection beats */}
      {inCatcher && (
        <button
          type="button"
          onClick={advance}
          data-testid="gesture-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
          style={{ zIndex: 88 }}
        >
          <span aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse">
            tap to continue &nbsp;·&nbsp; {catcherStep} / {catcherTotal}
          </span>
        </button>
      )}
    </div>
  );
}

export default RehearsalRoomGesture;
